import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './CardArchive.module.scss';
import notPhoto from '@assets/img/noPhotoNews.svg';
import deletePhotoImg from '@assets/img/AdminPanel/delete.svg';
import deletePhoto2Img from '@assets/img/AdminPanel/deletePhoto.svg';
import editPhoto2Img from '@assets/img/AdminPanel/editPhoto.svg';
import closeIcon from '@assets/img/AdminPanel/closeArchive.svg';
import errorIcon from '@assets/img/UI/error.svg';
import {
  apiDeleteMulti,
  deleteArchive,
  updateArchive,
  uploadPhoto,
} from '../../../../apirequests/apirequests';
import { server } from '../../../../apirequests/apirequests';
import ImageCropper from '../../../../components/ImageCropper/ImageCropper';
import ErrorModal from '../../../../components/ErrorModal/ErrorModal';

const ARCHIVE_EXTENSIONS = ['zip', 'rar', '7z'];
const MAX_ARCHIVE_SIZE = 1024 * 1024 * 1024; // 1 GB
const ERROR_FORMAT = 'Допустимый формат архива - .zip, .rar, .7z';
const ERROR_SIZE = 'Максимальный размер файла - 1Гб';
const ERROR_IMPORT = 'Ошибка при импорте файла';

const getArchiveUploadError = errorLike => {
  const data = errorLike?.response?.data || errorLike?.data;
  const errCode = data?.errCode;
  const message = data?.message || '';

  if (errCode === 'FileTooLarge' || /file too large|слишком больш|размер/i.test(message)) {
    return ERROR_SIZE;
  }
  if (
    errCode === 'FileExtensionNotAllowed' ||
    /extension|формат|not allowed/i.test(message)
  ) {
    return ERROR_FORMAT;
  }
  return ERROR_IMPORT;
};

function CardArchive({ item, updateData }) {
  const textareasRef = useRef(null);
  const buttonContainerRef = useRef(null);
  const buttonDeleteRef = useRef(null);
  const refFile = useRef(null);
  const archiveInputRef = useRef(null);
  const [editPhoto, setEditPhoto] = useState(false);
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [newArchiveFile, setNewArchiveFile] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [dataItem, setDataItem] = useState({
    file: item?.file || null,
    name: item?.name || '',
    url: item?.url || '',
    fileZip: item?.fileZip || null,
  });
  const [isChanged, setIsChanged] = useState(false);
  const [errorName, setErrorName] = useState('');
  const [errorUrl, setErrorUrl] = useState('');
  const [errorArchive, setErrorArchive] = useState('');

  useEffect(() => {
    setDataItem({
      file: item?.file || null,
      name: item?.name || '',
      url: item?.url || '',
      fileZip: item?.fileZip || null,
    });
    setNewArchiveFile(null);
    setErrorName('');
    setErrorUrl('');
    setErrorArchive('');
    if (archiveInputRef.current) archiveInputRef.current.value = null;
  }, [item]);

  useEffect(() => {
    setIsChanged(
      dataItem.name !== (item?.name || '') ||
        dataItem.url !== (item?.url || '') ||
        dataItem?.file?.id !== item?.file?.id ||
        dataItem?.fileZip?.id !== item?.fileZip?.id ||
        Boolean(newArchiveFile),
    );
  }, [dataItem, item, newArchiveFile]);

  useEffect(() => {
    if (buttonContainerRef.current) {
      gsap.to(buttonContainerRef.current, {
        opacity: isChanged ? 1 : 0,
        height: isChanged ? 'auto' : 0,
        duration: 0.3,
        ease: 'power2.out',
        display: isChanged ? 'flex' : 'none',
      });
    } else {
      gsap.fromTo(
        buttonDeleteRef.current,
        { height: 0 },
        { height: '52px', duration: 0.3, ease: 'power2.out' },
      );
    }
  }, [isChanged]);

  const handleEditData = (value, key) => {
    setDataItem(prev => ({ ...prev, [key]: value }));

    if (key === 'name' && errorName) {
      setErrorName('');
    }
    if (key === 'url') {
      setErrorUrl('');
      setErrorArchive('');
    }
  };

  const handleCancel = () => {
    setErrorName('');
    setErrorUrl('');
    setErrorArchive('');
    setNewArchiveFile(null);
    if (archiveInputRef.current) archiveInputRef.current.value = null;
    setDataItem({
      file: item?.file || null,
      name: item?.name || '',
      url: item?.url || '',
      fileZip: item?.fileZip || null,
    });
  };

  const validateArchiveFile = selected => {
    if (!selected) return false;
    const extension = selected.name.split('.').pop()?.toLowerCase();
    if (!ARCHIVE_EXTENSIONS.includes(extension)) {
      setErrorModal(ERROR_FORMAT);
      return false;
    }
    if (selected.size > MAX_ARCHIVE_SIZE) {
      setErrorModal(ERROR_SIZE);
      return false;
    }
    return true;
  };

  const handleArchiveChange = e => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!validateArchiveFile(selected)) {
      e.target.value = null;
      return;
    }
    setNewArchiveFile(selected);
    setErrorArchive('');
    setErrorUrl('');
  };

  const handleDeleteArchiveFile = async () => {
    if (newArchiveFile) {
      setNewArchiveFile(null);
      if (archiveInputRef.current) archiveInputRef.current.value = null;
      return;
    }

    if (!dataItem?.fileZip?.id) return;

    try {
      const res = await apiDeleteMulti({ ids: [dataItem.fileZip.id] });
      if (res?.status === 200) {
        setDataItem(prev => ({ ...prev, fileZip: null }));
        updateData();
      } else {
        setErrorModal(ERROR_IMPORT);
      }
    } catch (error) {
      console.error(error);
      setErrorModal(ERROR_IMPORT);
    }
  };

  const spliceFileName = fileName => {
    if (!fileName) return '';
    if (fileName.length > 28) {
      const extension = fileName.split('.').pop();
      return `${fileName.slice(0, 28)}...${extension}`;
    }
    return fileName;
  };

  const validateFields = () => {
    let valid = true;
    const trimmedUrl = (dataItem.url || '').trim();
    const hasUrl = trimmedUrl.length > 0;
    const hasArchive = Boolean(newArchiveFile || dataItem.fileZip);

    if (!dataItem.name) {
      setErrorName('Обязательное поле');
      valid = false;
    } else if (dataItem.name.length > 250) {
      setErrorName('Не более 250 символов*');
      valid = false;
    } else {
      setErrorName('');
    }

    // Нужно заполнить хотя бы одно: ссылку или файл архива
    if (!hasUrl && !hasArchive) {
      setErrorUrl('Обязательное поле');
      setErrorArchive('');
      valid = false;
    } else {
      setErrorUrl('');
      setErrorArchive('');
    }

    if (trimmedUrl.length > 250) {
      setErrorUrl('Не более 250 символов*');
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const trimmedUrl = (dataItem.url || '').trim();

    try {
      const res = await updateArchive(
        {
          name: dataItem.name,
          url: trimmedUrl || null,
          type: 0,
        },
        item.id,
      );

      if (res?.status === 200) {
        if (newArchiveFile) {
          const formData = new FormData();
          formData.append('file', newArchiveFile);
          formData.append('archiveId', item.id);
          const uploadResp = await uploadPhoto(formData, 'ARCHIVE_ZIP');
          if (uploadResp?.status !== 200) {
            setErrorModal(getArchiveUploadError(uploadResp));
            return;
          }
        }
        setNewArchiveFile(null);
        if (archiveInputRef.current) archiveInputRef.current.value = null;
        updateData();
      }
    } catch (error) {
      console.error(error);
      setErrorModal(getArchiveUploadError(error));
    }
  };

  const handleDelete = () => {
    setConfirmDelete(true);
  };

  const confirmDeleteAlbum = () => {
    deleteArchive(item.id).then(res => {
      if (res?.status === 200) {
        setConfirmDelete(false);
        updateData();
      }
    });
  };

  const handleFileChange = e => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const extension = selected.name.split('.').pop()?.toLowerCase();
    const allowedExt = ['png', 'jpg', 'jpeg'];
    if (!allowedTypes.includes(selected.type) && !allowedExt.includes(extension)) {
      setErrorModal('Допустимый формат изображения - .png, .jpg, .jpeg');
      e.target.value = null;
      return;
    }

    setEditPhoto(true);
    setFile(selected);
    setUrl(URL.createObjectURL(selected));
  };

  const handleDeleteImg = item => {
    apiDeleteMulti({ ids: [item?.file?.id] }).then(res => {
      if (res?.status === 200) updateData();
    });
  };

  const funEditPhoto = file => {
    if (file) {
      setEditPhoto(false);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('archiveId', item?.id);

      uploadPhoto(formData, 'PHOTO_ARCHIVE').then(res => {
        if (res?.status === 200) {
          setDataItem(prev => ({ ...prev, file: res.data.file }));
          refFile.current.value = null;
          updateData();
          setFile(null);
          setUrl(null);
        }
      });
    }
  };

  const archiveDisplayName = newArchiveFile?.name || dataItem?.fileZip?.name;

  return (
    <>
      <ImageCropper
        editPhoto={editPhoto}
        setEditPhoto={setEditPhoto}
        urlPhoto={url}
        funEditPhoto={funEditPhoto}
        circularCrop={false}
      />
      <ErrorModal open={Boolean(errorModal)} close={() => setErrorModal(null)} title={errorModal} />
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles.confirmBlur}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <p className={styles.confirmTitle}>Вы уверены, что хотите удалить альбом?</p>
              <img className={styles.confirmIcon} src={errorIcon} alt="" />
              <div className={styles.confirmBtns}>
                <button type="button" onClick={() => setConfirmDelete(false)}>
                  Отмена
                </button>
                <button type="button" onClick={confirmDeleteAlbum}>
                  Удалить
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.CardOrganization} key={item.id}>
        <div className={styles.CardOrganizationInner}>
          <div className={styles.CardOrganizationInnerImg}>
            <img
              className={styles.Img}
              src={url ? url : dataItem?.file?.url ? `${server}/${dataItem?.file?.url}` : notPhoto}
              alt="Фото"
            />
            <div className={styles.CardOrganizationInnerImgInput}>
              <img src={deletePhoto2Img} onClick={() => handleDeleteImg(item)} alt="Удалить" />
              <img
                src={editPhoto2Img}
                alt="Редактировать"
                onClick={() => {
                  refFile.current.click();
                }}
              />
            </div>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              style={{ display: 'none' }}
              ref={refFile}
              onChange={handleFileChange}
            />
          </div>
          <div className={styles.CardOrganizationInnerInfo}>
            <label>Название альбома</label>
            <input
              value={dataItem.name}
              onChange={e => handleEditData(e.target.value, 'name')}
              style={{ borderColor: errorName ? '#B32020' : '' }}
            />
            {errorName && <span className={styles.error}>{errorName}</span>}
          </div>
          <div className={styles.CardOrganizationInnerInfo}>
            <label>Ссылка</label>
            <textarea
              ref={textareasRef}
              value={dataItem.url}
              onChange={e => handleEditData(e.target.value, 'url')}
              style={{ borderColor: errorUrl ? '#B32020' : '' }}
            />
            {errorUrl && <span className={styles.error}>{errorUrl}</span>}
          </div>
          <div className={styles.CardOrganizationInnerInfo}>
            <label>Файл архива</label>
            <input
              type="file"
              ref={archiveInputRef}
              accept=".zip,.rar,.7z"
              style={{ display: 'none' }}
              onChange={handleArchiveChange}
            />
            {archiveDisplayName ? (
              <div className={styles.archiveChip}>
                <span>{spliceFileName(archiveDisplayName)}</span>
                <button type="button" onClick={handleDeleteArchiveFile} aria-label="Удалить файл">
                  <img src={closeIcon} alt="" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.archiveUploadBtn}
                onClick={() => archiveInputRef.current?.click()}
                style={{ borderColor: errorArchive ? '#B32020' : '' }}
              >
                + Загрузите архив
              </button>
            )}
            {errorArchive && <span className={styles.error}>{errorArchive}</span>}
          </div>
          {isChanged ? (
            <div className={styles.buttonContainer} ref={buttonContainerRef}>
              <button className={styles.cancel} onClick={handleCancel}>
                Отмена
              </button>
              <button className={styles.save} onClick={handleSave}>
                Сохранить
              </button>
            </div>
          ) : (
            <button className={styles.delete} onClick={handleDelete} ref={buttonDeleteRef}>
              Удалить <img src={deletePhotoImg} alt="Удалить" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default CardArchive;
